import { useQuery } from "@tanstack/react-query";
import { Stethoscope, FlaskConical, Pill } from "lucide-react";

import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../../shared/api/hisIntegration";
import { patientsApi } from "../../../shared/api/patients";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * "Lịch sử khám từ HIS" — structured clinical history pulled on demand from the external HIS
 * (HISTORY capability, ADR-0020). Each encounter shows visit metadata + diagnosis, its technical
 * services (cận lâm sàng) and prescribed drugs. Populated by connectors that expose structured
 * history (Leo); empty for MQ (which uses signed-PDF HSBA on a separate card) → card hides.
 */
export function HisMedicalHistoryCard({
  patientId,
  patientCode,
  hasHis,
}: {
  patientId: string;
  patientCode: string | null | undefined;
  hasHis: boolean;
}) {
  const identifiers = useQuery({
    queryKey: ["patient", patientId, "identifiers-for-his"],
    queryFn: () => patientsApi.listIdentifiers(patientId),
    enabled: hasHis && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
  const cccd =
    (identifiers.data ?? [])
      .filter((x) => x.identifierType === "CCCD")
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))[0]?.value ?? null;

  const key = cccd || patientCode || "";
  const q = useQuery({
    queryKey: hisIntegrationQueryKeys.medicalHistory(key),
    queryFn: () =>
      hisIntegrationApi.medicalHistory({ socialId: cccd ?? undefined, patientCode: patientCode ?? undefined }),
    enabled: hasHis && !!key,
    staleTime: 60_000,
  });

  if (!hasHis || !key) return null;
  const history = q.data ?? [];
  // Hide entirely when this connector has no structured history (e.g. MQ) — no empty card noise.
  if (!q.isLoading && !q.isError && history.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Stethoscope aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        <CardTitle>Lịch sử khám từ HIS</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="m-0 text-sm text-muted-foreground">Đang tải lịch sử khám…</p>}
        {q.isError && (
          <p className="m-0 text-sm text-destructive">Không lấy được lịch sử khám từ HIS.</p>
        )}
        <ul className="m-0 list-none space-y-3 p-0">
          {history.map((e, i) => (
            <li key={i} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                {e.examDate ?? "—"}
                {e.clinicRoom && <Badge variant="muted">{e.clinicRoom}</Badge>}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {[e.doctorName, e.diagnosis].filter(Boolean).join(" · ") || "—"}
              </div>

              {e.services.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                    <FlaskConical aria-hidden="true" className="h-3 w-3" /> Cận lâm sàng
                  </div>
                  <ul className="m-0 mt-1 list-none space-y-0.5 p-0">
                    {e.services.map((s, j) => (
                      <li key={j} className="flex justify-between gap-3 text-sm">
                        <span>{s.name ?? "—"}</span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          SL {s.quantity ?? "—"} {s.unit ?? ""}
                          {s.resultImage && (
                            <a
                              href={s.resultImage}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline"
                            >
                              Xem KQ
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {e.drugs.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                    <Pill aria-hidden="true" className="h-3 w-3" /> Toa thuốc
                  </div>
                  <ul className="m-0 mt-1 list-none space-y-0.5 p-0">
                    {e.drugs.map((d, j) => (
                      <li key={j} className="text-sm">
                        <span className="font-medium text-foreground">{d.name ?? "—"}</span>{" "}
                        <span className="text-xs text-muted-foreground">
                          — SL {d.quantity ?? "—"} {d.unit ?? ""}
                          {d.daysOfUse ? ` · ${d.daysOfUse} ngày` : ""}
                          {d.instruction ? ` · ${d.instruction}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
